import type { Connection, SaveConnectionOptions } from '../../../types/electron';

/**
 * The SSH secrets the connection dialog collects — a bastion password, a key
 * passphrase — are FORM-ONLY. `SshTunnelConfig` documents them as "transient,
 * never persisted", and since round 5 (F5) they travel beside a save as
 * `SaveConnectionOptions.sshPassword` / `.sshPassphrase` rather than on the
 * record, for the same reason `passwordEdited` does: the store keeps whatever
 * connection object it is handed, and every later background re-save presented
 * the plaintext to the reset predicate as "a credential the user just typed",
 * tearing the live pool and tunnel down mid-work.
 *
 * Two helpers, one per direction:
 *  - `stripTransientSshSecrets` — before a connection is saved or stored: the
 *    record never carries them.
 *  - `withTransientSshSecrets` — at DIAL time, for a secret the user typed but
 *    chose not to keep in the keychain: the tunnel manager reads the typed
 *    value off `sshTunnel` when no stored blob exists, so the store attaches
 *    what it remembered for this session, to the dial config only.
 */
export interface TransientSshSecrets {
  password?: string;
  passphrase?: string;
}

/**
 * The SSH TARGET a transient secret belongs to (A5).
 *
 * A secret typed into the dialog is bound to the bastion it was typed for. The
 * store used to key them by connection id alone, so editing a connection's SSH
 * target and dialling again handed the OLD bastion's password to the NEW one —
 * the exact rebinding `sshSecretsSave.ts`'s identity tag exists to prevent for
 * STORED secrets, missing for typed ones.
 *
 * The same fields main hashes into a v2 identity tag (host, user, auth kind,
 * key path, port), joined rather than hashed: this key never leaves renderer
 * memory, where the connection record already holds all of them in the clear,
 * and a Web build has no `node:crypto`. NUL-joined so `host='ab'+user='c'`
 * cannot collide with `host='a'+user='bc'` — the same rule as the main-side
 * digest.
 *
 * `undefined` when there is no SSH block: there is no target, so there is
 * nothing to bind and nothing to remember.
 */
export function sshTunnelIdentityKey(connection: Connection): string | undefined {
  const tunnel = connection.sshTunnel;
  if (!tunnel) return undefined;
  const auth = tunnel.auth as { kind?: string; keyPath?: string } | undefined;
  const port = typeof tunnel.port === 'number' && Number.isFinite(tunnel.port) ? String(tunnel.port) : '';
  return [tunnel.host ?? '', tunnel.user ?? '', auth?.kind ?? '', auth?.keyPath ?? '', port].join('\u0000');
}

export function stripTransientSshSecrets(connection: Connection): Connection {
  if (!connection.sshTunnel) return connection;
  const { password: _password, passphrase: _passphrase, ...sshTunnel } = connection.sshTunnel;
  return { ...connection, sshTunnel };
}

/** The typed SSH secrets a save carries, or `undefined` when it carries none. */
export function transientSshSecretsFromOptions(options: SaveConnectionOptions | undefined): TransientSshSecrets | undefined {
  const password = options?.sshPassword;
  const passphrase = options?.sshPassphrase;
  if (!password && !passphrase) return undefined;
  return { ...(password ? { password } : {}), ...(passphrase ? { passphrase } : {}) };
}

/** The dial config: the connection with this session's typed SSH secrets attached. */
export function withTransientSshSecrets(connection: Connection, secrets: TransientSshSecrets | undefined): Connection {
  if (!secrets || !connection.sshTunnel?.enabled) return connection;
  const current = connection.sshTunnel as { password?: string; passphrase?: string };
  return {
    ...connection,
    sshTunnel: {
      ...connection.sshTunnel,
      ...(secrets.password && !current.password ? { password: secrets.password } : {}),
      ...(secrets.passphrase && !current.passphrase ? { passphrase: secrets.passphrase } : {}),
    },
  };
}
